import { useMutation } from "@apollo/client";

import DocumentationDetachmentStatus from "$/settings/enums/machine/template-documentation-status.json";
import {
  ASSIGN_ASSETS_TO_PARENT_MUTATION,
  ASSIGN_MULTIPLE_ASSETS_TO_TEAM_MUTATION,
  ASSIGN_OWN_OEM_MULTIPLE_ASSETS_TO_OWN_OEM_CUSTOMER,
  ASSIGN_OWN_OEM_PARTS_TO_ASSET_MUTATION,
  BULK_DELETE_OWN_OEM_ASSETS_MUTATION,
  CREATE_OWN_OEM_ASSET_MUTATION,
  DELETE_OEM_ASSET_MUTATION,
  DELETE_OWN_OEM_ASSET_IMAGE,
  GET_OWN_OEM_ASSET_BY_ID_QUERY,
  GET_OWN_OEM_CUSTOMER_BY_ID_QUERY,
  HANDLE_OWN_OEM_ASSET_QR_ACCESS_MUTATION,
  REMOVE_OWN_OEM_ASSET_FROM_OWN_OEM_CUSTOMER,
  REMOVE_OWN_OEM_PART_FROM_ASSET_MUTATION,
  UNASSIGN_ASSET_FROM_PARENT_MUTATION,
  UNASSIGN_ASSET_FROM_TEAM_MUTATION,
  UPDATE_OWN_OEM_ASSET_MUTATION,
  CREATE_MACHINE_HISTORY_NOTE_MUTATION,
  UPDATE_MACHINE_HISTORY_NOTE_MUTATION,
  DELETE_MACHINE_HISTORY_NOTE_MUTATION,
  MachineHistory,
  DETACH_OWN_OEM_ASSET_DOCUMENTATION,
  RESET_OWN_OEM_ASSET_TO_TEMPLATE,
  Asset,
  ASSIGN_OWN_OEM_MULTIPLE_ASSETS_TO_OWN_OEM_CUSTOMER_PORTAL,
  REMOVE_OWN_OEM_ASSET_FROM_OWN_OEM_CUSTOMER_PORTAL,
  ASSIGN_PROCEDURES_TO_ASSET_MUTATION,
  UNASSIGN_PROCEDURE_FROM_ASSET_MUTATION,
} from "~/api";
import client from "~/apollo/_client";
import { errorsHandler } from "~/transformers";

export const updateAssetCache = (payload) => {
  const asset = client.readQuery({
    query: GET_OWN_OEM_ASSET_BY_ID_QUERY,
    variables: { id: payload._id },
  });

  if (!asset) return;

  const { getOwnOemAssetById: cachedAsset } = asset;
  if (cachedAsset) {
    const cloned = { ...cachedAsset };
    cloned.documentFolders = {
      ...(cloned.documentFolders ? cloned.documentFolders : {}),
      ...payload.documentFolders,
    };

    client.writeQuery({
      query: GET_OWN_OEM_ASSET_BY_ID_QUERY,
      variables: { id: payload._id },
      data: {
        getOwnOemAssetById: cloned,
      },
    });
  }
};

export const useUpdateAssetQuery = () => {
  const handleUpdateAssetQuery = ({ id, success = false }) => {
    // @ts-ignore
    const { getOwnOemAssetById } = client.cache.readQuery({
      query: GET_OWN_OEM_ASSET_BY_ID_QUERY,
      variables: {
        id,
      },
    });

    if (success) {
      client.cache.writeQuery({
        query: GET_OWN_OEM_ASSET_BY_ID_QUERY,
        variables: {
          id: id,
        },
        data: {
          getOwnOemAssetById: {
            ...getOwnOemAssetById,
            detachedFromTemplate: {
              ...getOwnOemAssetById.detachedFromTemplate,
              documentation: DocumentationDetachmentStatus.detached,
            },
          },
        },
      });
    } else {
      client.cache.evict({
        id: "ROOT_QUERY",
        fieldName: "getOwnOemAssetById",
      });
      client.cache.gc();
    }
  };
  return { handleUpdateAssetQuery };
};

export const useCreateMachineHistoryNote = () => {
  const [createMachineHistoryNote, { error = {}, loading }] = useMutation(
    CREATE_MACHINE_HISTORY_NOTE_MUTATION,
  );
  return {
    mutation: (data) =>
      new Promise((resolve, reject) => {
        return createMachineHistoryNote({
          variables: {
            input: {
              ...data,
            },
          },
          update: (cache) => {
            cache.evict({
              id: "ROOT_QUERY",
              fieldName: "listOwnOemMachineHistory",
            });
            cache.gc();
          },
        })
          .then((data) => {
            resolve(data);
          })
          .catch((err) => {
            reject(errorsHandler(err));
          });
      }),
    loading,
    error,
  };
};

export const useAssignProceduresToAsset = () => {
  const [assign, { loading, error }] = useMutation(
    ASSIGN_PROCEDURES_TO_ASSET_MUTATION,
  );

  return {
    assignProceduresToAsset: async (input: {
      procedures: string[];
      assetId: string;
    }) =>
      new Promise((resolve, reject) => {
        return assign({
          variables: { input },
          refetchQueries: [
            {
              query: GET_OWN_OEM_ASSET_BY_ID_QUERY,
              variables: { id: input.assetId },
            },
          ],
          awaitRefetchQueries: true,
          update(cache) {
            cache.evict({
              id: "ROOT_QUERY",
              fieldName: "listOwnOemProcedureTemplatesWithCount",
            });
            cache.gc();
          },
        })
          .then((data) => {
            resolve(data);
          })
          .catch((err) => {
            reject(errorsHandler(err));
          });
      }),
    loading,
    error,
  };
};

export const useUnassignProcedureFromAsset = () => {
  const [unassign, { loading, error }] = useMutation(
    UNASSIGN_PROCEDURE_FROM_ASSET_MUTATION,
  );

  return {
    unassignProcedureFromAsset: async (input: {
      assetId: string;
      procedureId: string;
    }) =>
      new Promise((resolve, reject) => {
        return unassign({
          variables: { input },
          refetchQueries: [
            {
              query: GET_OWN_OEM_ASSET_BY_ID_QUERY,
              variables: { id: input.assetId },
            },
          ],
          awaitRefetchQueries: true,
          update(cache) {
            cache.evict({
              id: "ROOT_QUERY",
              fieldName: "listOwnOemProcedureTemplatesWithCount",
            });
            cache.gc();
          },
        })
          .then((data) => {
            resolve(data);
          })
          .catch((err) => {
            reject(errorsHandler(err));
          });
      }),
    loading,
    error,
  };
};

export const useUpdateMachineHistoryNote = () => {
  const [updateMachineHistoryNote, { error = {}, loading }] = useMutation(
    UPDATE_MACHINE_HISTORY_NOTE_MUTATION,
  );

  return {
    mutation: (data) =>
      new Promise((resolve, reject) => {
        return updateMachineHistoryNote({
          variables: {
            input: {
              ...data,
            },
          },

          update(cache, { data: { updateMachineHistoryNote: ref } }) {
            cache.modify({
              id: cache.identify({
                __typename: "MachineHistory",
                id: ref._id,
              }),
              fields: {
                listOwnOemMachineHistory(existingRefs = []) {
                  const newRef = cache.writeFragment({
                    data: ref,
                    fragment: MachineHistory.fragments.MachineHistoryData,
                    fragmentName: "MachineHistoryData",
                  });

                  return [...existingRefs, newRef];
                },
              },
            });
          },
        })
          .then((data) => {
            resolve(data);
          })
          .catch((err) => {
            reject(errorsHandler(err));
          });
      }),
    loading,
    error,
  };
};

export const useDeleteMachineHistoryNote = () => {
  const [remove, { loading, error }] = useMutation(
    DELETE_MACHINE_HISTORY_NOTE_MUTATION,
  );

  return {
    loading,
    error,
    removeHistoryNote: async (historyNoteId, machineId, aiNoteId) =>
      new Promise((resolve, reject) =>
        remove({
          variables: { id: historyNoteId, machine: machineId },
          // @ts-ignore
          skip: !historyNoteId,
          update(cache) {
            aiNoteId &&
              cache.modify({
                id: cache.identify({
                  __typename: "AiNote",
                  _id: aiNoteId,
                }),
                fields: {
                  publishedMachines(existingPublishedMachines, { readField }) {
                    if (!existingPublishedMachines)
                      return existingPublishedMachines;

                    return existingPublishedMachines.filter(
                      (machine) =>
                        readField("machineHistoryNoteId", machine) !==
                        historyNoteId,
                    );
                  },
                },
              });

            cache.evict({
              id: "ROOT_QUERY",
              fieldName: "listOwnOemMachineHistory",
            });
            cache.evict({
              id: "ROOT_QUERY",
              fieldName: "listOwnOemActivityLogs",
            });
            cache.gc();
          },
        })
          .then((data) => {
            resolve(data);
          })
          .catch((error) => {
            reject(errorsHandler(error));
          }),
      ),
  };
};

export const useDetachAssetDocumentation = () => {
  const [detachOwnOemAssetDocumentation, { loading, error }] = useMutation(
    DETACH_OWN_OEM_ASSET_DOCUMENTATION,
  );

  return {
    mutation: (_id) =>
      new Promise((resolve, reject) => {
        return detachOwnOemAssetDocumentation({
          variables: {
            _id,
          },
        })
          .then((data) => {
            resolve(data);
          })
          .catch((err) => {
            reject(errorsHandler(err));
          });
      }),
    loading,
    error,
  };
};

export const handleRefetchAssetDocumentationFolders = () => {
  client.cache.evict({
    id: "ROOT_QUERY",
    fieldName: "getOwnOemAssetById",
  });
  client.cache.gc();
};

export const useResetAssetToTemplate = () => {
  const [resetOwnOemAssetToTemplate, { loading, error }] = useMutation(
    RESET_OWN_OEM_ASSET_TO_TEMPLATE,
  );

  return {
    mutation: (input) =>
      new Promise((resolve, reject) => {
        return resetOwnOemAssetToTemplate({
          variables: { input },
          update(cache, { data: { resetOwnOemAssetToTemplate: ref } }) {
            const cacheId = cache.identify({
              __typename: "Asset",
              id: input.assetId,
            });
            const assetRef = cache.readFragment({
              id: cacheId,
              fragment: Asset.fragments.AssetFullData,
              fragmentName: "AssetFullData",
            });

            if (!assetRef) return;

            cache.writeFragment({
              id: cacheId,
              fragment: Asset.fragments.AssetFullData,
              fragmentName: "AssetFullData",
              data: {
                ...ref,
                ...((input.fieldsToReset || []).includes("documentation")
                  ? {
                      documentFolders: {
                        ...ref.documentFolders,
                        externalId: null,
                        internalId: null,
                      },
                    }
                  : {}),
              },
            });
            cache.evict({
              id: "ROOT_QUERY",
              fieldName: "listOwnOemProcedureTemplatesWithCount",
            });
            cache.gc();
          },
        })
          .then((data) => {
            resolve(data);
          })
          .catch((err) => {
            reject(errorsHandler(err));
          });
      }),
    loading,
    error,
  };
};

export const useAssignAssetParts = () => {
  const [add, { loading, error }] = useMutation(
    ASSIGN_OWN_OEM_PARTS_TO_ASSET_MUTATION,
  );

  return {
    addNewParts: async (input) =>
      add({
        variables: { input },
        // @ts-ignore
        skip: !input?.assetId,
        update(cache) {
          cache.evict({
            id: "ROOT_QUERY",
            fieldName: "getOwnOemAssetById",
          });
          cache.gc();
        },
      }),
    loading,
    error,
  };
};

export const useAssignAssetsToCustomer = () => {
  const [assign, { loading, error }] = useMutation(
    ASSIGN_OWN_OEM_MULTIPLE_ASSETS_TO_OWN_OEM_CUSTOMER,
  );

  return {
    error,
    assigningAssets: loading,
    mutation: (input) =>
      new Promise((resolve, reject) => {
        return assign({
          variables: { input },
          update(cache) {
            cache.evict({
              id: "ROOT_QUERY",
              fieldName: "getOwnOemCustomerById",
            });
            cache.evict({
              id: "ROOT_QUERY",
              fieldName: "listAllOwnOemAssets",
            });
            cache.gc();
          },
        })
          .then((data) => {
            resolve(data);
          })
          .catch((err) => {
            reject(errorsHandler(err));
          });
      }),
  };
};

export const useAssignAssetsToCustomerPortal = () => {
  const [assign, { loading, error }] = useMutation<{
    assignOwnOemMultipleAssetsToOwnOemCustomerPortal: string;
  }>(ASSIGN_OWN_OEM_MULTIPLE_ASSETS_TO_OWN_OEM_CUSTOMER_PORTAL);

  return {
    error,
    assigningAssets: loading,
    mutation: (input: { assets: string[]; portal: string }) =>
      new Promise((resolve, reject) => {
        return assign({
          variables: { input },
          update(cache) {
            cache.evict({
              id: "ROOT_QUERY",
              fieldName: "listAllOwnOemAssets",
            });
            cache.gc();
          },
        })
          .then((data) => {
            resolve(data);
          })
          .catch((err) => {
            reject(errorsHandler(err));
          });
      }),
  };
};

export const useAssignAssetsToParent = () => {
  const [assign, { loading, error }] = useMutation(
    ASSIGN_ASSETS_TO_PARENT_MUTATION,
  );

  return {
    mutation: ({
      assetIds,
      parentId,
    }: {
      assetIds: Array<string>;
      parentId: string;
    }) =>
      new Promise((resolve, reject) => {
        return assign({
          variables: {
            input: { assetIds, parentId },
          },
          update(cache) {
            cache.evict({
              id: "ROOT_QUERY",
              fieldName: "getOwnOemAssetById",
            });
            cache.evict({
              id: "ROOT_QUERY",
              fieldName: "listAllOwnOemAssets",
            });
            cache.gc();
          },
        })
          .then((data) => {
            resolve(data);
          })
          .catch((err) => {
            reject(errorsHandler(err));
          });
      }),
    loading,
    error,
  };
};

export const useAssignAssetsToTeam = () => {
  const [assign, { loading, error }] = useMutation(
    ASSIGN_MULTIPLE_ASSETS_TO_TEAM_MUTATION,
  );

  return {
    error,
    loading,
    mutation: (input, options: { skipCacheUpdate?: boolean } = {}) =>
      new Promise((resolve, reject) => {
        const { skipCacheUpdate = false } = options;

        assign({
          variables: { input },
          // @ts-ignore
          skip: !input?.team,
          update(cache) {
            if (!skipCacheUpdate) {
              cache.evict({
                id: "ROOT_QUERY",
                fieldName: "listAllOwnOemAssets",
              });
              cache.evict({
                id: "ROOT_QUERY",
                fieldName: "listAllOwnOemCustomers",
              });
              cache.evict({
                id: "ROOT_QUERY",
                fieldName: "listOwnOemCustomers",
              });
            } else {
              // When skipping cache update, manually update each asset's teams in the cache
              const assetIds = input.assets || [];
              const teamId = input.team;

              assetIds.forEach((assetId) => {
                cache.modify({
                  id: cache.identify({ __typename: "Asset", _id: assetId }),
                  fields: {
                    teams(existingTeams = []) {
                      // Check if team is already in the list
                      const teamExists = existingTeams.some(
                        (team) =>
                          (typeof team === "string" ? team : team._id) ===
                          teamId,
                      );

                      if (teamExists) {
                        return existingTeams;
                      }

                      // Add the team reference
                      return [...existingTeams, { __ref: `Team:${teamId}` }];
                    },
                  },
                });
              });
            }
          },
        })
          .then((result) => {
            resolve(result);
          })
          .catch((err) => {
            reject(errorsHandler(err));
          });
      }),
  };
};

export const useCreateAsset = () => {
  const [createOwnOemAsset, { error = {}, loading }] = useMutation(
    CREATE_OWN_OEM_ASSET_MUTATION,
  );

  return {
    mutation: (data) =>
      new Promise((resolve, reject) => {
        return createOwnOemAsset({
          variables: {
            input: {
              ...data,
            },
          },
          update: (cache) => {
            cache.evict({
              id: "ROOT_QUERY",
              fieldName: "listAllOwnOemAssets",
            });
            cache.evict({
              id: "ROOT_QUERY",
              fieldName: "listOwnOemKanbanAssets",
            });
            cache.gc();
          },
        })
          .then((data) => {
            resolve(data);
          })
          .catch((err) => {
            reject(errorsHandler(err));
          });
      }),
    loading,
    error,
  };
};

export const useDeleteAsset = () => {
  const [remove, { loading, error }] = useMutation(DELETE_OEM_ASSET_MUTATION);

  return {
    loading,
    error,
    removeAsset: async (assetId: string) =>
      new Promise((resolve, reject) =>
        remove({
          variables: { assetId },
          // @ts-ignore
          skip: !assetId,
          update(cache) {
            cache.evict({
              id: "ROOT_QUERY",
              fieldName: "listAllOwnOemAssets",
            });
            cache.gc();
          },
          refetchQueries: [GET_OWN_OEM_CUSTOMER_BY_ID_QUERY],
        })
          .then((data) => {
            resolve(data);
          })
          .catch((error) => {
            reject(error);
          }),
      ),
  };
};

export const useDeleteAssetImage = () => {
  const [remove, { loading, error }] = useMutation(DELETE_OWN_OEM_ASSET_IMAGE);

  return {
    loading,
    error,
    deleteImage: async (assetId) =>
      remove({
        variables: { assetId },
      }),
  };
};

export const useHandleAssetQRAccess = () => {
  const [handleOwnOemAssetQRAccess, { error = {}, loading }] = useMutation(
    HANDLE_OWN_OEM_ASSET_QR_ACCESS_MUTATION,
  );

  return {
    mutation: ({ assetId, isQRCodeEnabled }) =>
      new Promise((resolve, reject) => {
        return handleOwnOemAssetQRAccess({
          variables: {
            input: {
              assetId,
              isQRCodeEnabled,
            },
          },
        })
          .then((data) => {
            resolve(data);
          })
          .catch((err) => {
            reject(errorsHandler(err));
          });
      }),
    loading,
    error,
  };
};

export const useRemoveAssetFromCustomer = () => {
  const [remove, { loading, error }] = useMutation(
    REMOVE_OWN_OEM_ASSET_FROM_OWN_OEM_CUSTOMER,
  );

  return {
    loading,
    error,
    mutation: (input) =>
      new Promise((resolve, reject) => {
        return remove({
          variables: { input },
          update(cache) {
            cache.evict({
              id: "ROOT_QUERY",
              fieldName: "getOwnOemCustomerById",
            });
            cache.evict({
              id: "ROOT_QUERY",
              fieldName: "listAllOwnOemAssets",
            });
            cache.gc();
          },
        })
          .then((data) => {
            resolve(data);
          })
          .catch((err) => {
            reject(errorsHandler(err));
          });
      }),
  };
};

export const useRemoveAssetFromCustomerPortal = () => {
  const [remove, { loading, error }] = useMutation<{
    removeOwnOemAssetFromOwnOemCustomerPortal: string;
  }>(REMOVE_OWN_OEM_ASSET_FROM_OWN_OEM_CUSTOMER_PORTAL);

  return {
    loading,
    error,
    mutation: (input: { asset: string; portal: string }) =>
      new Promise((resolve, reject) => {
        return remove({
          variables: { input },
          update(cache) {
            cache.evict({
              id: "ROOT_QUERY",
              fieldName: "listAllOwnOemAssets",
            });
            cache.gc();
          },
        })
          .then((data) => {
            resolve(data);
          })
          .catch((err) => {
            reject(errorsHandler(err));
          });
      }),
  };
};

export const useRemoveAssetPart = () => {
  const [remove, { loading, error }] = useMutation(
    REMOVE_OWN_OEM_PART_FROM_ASSET_MUTATION,
  );

  return {
    removePart: async (input) =>
      remove({
        variables: { input },
        // @ts-ignore
        skip: !input?.assetId || !input?.partId,
        update(cache) {
          cache.evict({
            id: "ROOT_QUERY",
            fieldName: "getOwnOemAssetById",
          });
          cache.gc();
        },
      }),
    loading,
    error,
  };
};

export const useUnassignAssetFromParent = () => {
  const [unassign, { loading, error }] = useMutation(
    UNASSIGN_ASSET_FROM_PARENT_MUTATION,
  );

  return {
    mutation: ({ assetId, parentId }: { assetId: string; parentId: string }) =>
      new Promise((resolve, reject) => {
        return unassign({
          variables: { input: { assetId, parentId } },
          update(cache) {
            cache.evict({
              id: "ROOT_QUERY",
              fieldName: "getOwnOemAssetById",
            });
            cache.evict({
              id: "ROOT_QUERY",
              fieldName: "listAllOwnOemAssets",
            });
            cache.gc();
          },
        })
          .then((data) => {
            resolve(data);
          })
          .catch((err) => {
            reject(errorsHandler(err));
          });
      }),
    loading,
    error,
  };
};

export const useUnassignAssetFromTeam = () => {
  const [remove, { loading, error }] = useMutation(
    UNASSIGN_ASSET_FROM_TEAM_MUTATION,
  );

  return {
    loading,
    error,
    mutation: (input, options: { skipCacheUpdate?: boolean } = {}) =>
      new Promise((resolve, reject) => {
        const { skipCacheUpdate = false } = options;

        return remove({
          variables: { input },
          // @ts-ignore
          skip: !input?.team || !input?.asset,
          update(cache) {
            if (!skipCacheUpdate) {
              cache.evict({
                id: "ROOT_QUERY",
                fieldName: "listAllOwnOemAssets",
              });

              cache.evict({
                id: "ROOT_QUERY",
                fieldName: "listOwnOemAllTickets",
              });
              cache.gc();
            } else {
              // When skipping cache update, manually remove the team from the asset's teams in the cache
              const assetId = input.asset;
              const teamId = input.team;

              cache.modify({
                id: cache.identify({ __typename: "Asset", _id: assetId }),
                fields: {
                  teams(existingTeams = [], { readField }) {
                    // Filter out the team being removed
                    return existingTeams.filter((team) => {
                      const id =
                        typeof team === "string"
                          ? team
                          : readField("_id", team);
                      return id !== teamId;
                    });
                  },
                },
              });
            }
          },
        })
          .then((res) => {
            resolve(res);
          })
          .catch((err) => {
            reject(errorsHandler(err));
          });
      }),
  };
};

type UpdateAssetOptions =
  | boolean
  | { evictCustomerCache?: boolean; skipCacheUpdate?: boolean };

export const useUpdateAsset = () => {
  const [updateOwnOemAsset, { error = {}, loading }] = useMutation(
    UPDATE_OWN_OEM_ASSET_MUTATION,
  );

  return {
    mutation: (data, options: UpdateAssetOptions = {}) =>
      new Promise((resolve, reject) => {
        const normalizedOptions =
          typeof options === "boolean"
            ? { evictCustomerCache: options }
            : options || {};
        const { evictCustomerCache = false, skipCacheUpdate = false } =
          normalizedOptions;

        return updateOwnOemAsset({
          variables: {
            input: {
              ...data,
            },
          },
          update(cache) {
            if (skipCacheUpdate) {
              // When skipping full cache update, Apollo will still automatically
              // normalize and update the asset in the cache since the mutation
              // returns the full asset data with AssetFullData fragment
              // We just don't evict queries to prevent full table refresh
              return;
            }
            if (evictCustomerCache)
              cache.evict({
                id: "ROOT_QUERY",
                fieldName: "getOwnOemCustomerById",
              });

            cache.evict({
              id: "ROOT_QUERY",
              fieldName: "listAllOwnOemAssets",
            });
            cache.evict({
              id: "ROOT_QUERY",
              fieldName: "listOwnOemAllTickets",
            });
            cache.gc();
          },
        })
          .then((data) => {
            resolve(data);
          })
          .catch((err) => {
            reject(errorsHandler(err));
          });
      }),
    loading,
    error,
  };
};

export const useBulkDeleteAssets = () => {
  const [bulkDelete] = useMutation(BULK_DELETE_OWN_OEM_ASSETS_MUTATION);

  const bulkDeleteAssets = async (
    assetIds: string[],
    deleteSubAssets = false,
  ) => {
    const { data } = await bulkDelete({
      variables: { assetIds, deleteSubAssets },
      update(cache) {
        cache.evict({ id: "ROOT_QUERY", fieldName: "listAllOwnOemAssets" });
        // Board view keeps its own column state seeded from this query —
        // evict so a refetch after delete doesn't resurrect removed cards.
        cache.evict({
          id: "ROOT_QUERY",
          fieldName: "listOwnOemKanbanAssets",
        });
        cache.gc();
      },
    });
    return data.bulkDeleteOwnOemAssets;
  };

  return { bulkDeleteAssets };
};
